/**
 * HelperText - Reusable helper text component for listing pages
 * Displays contextual information with an icon and styled container
 */
import { alpha, Box, Paper, Stack, SxProps, Theme, Typography } from '@mui/material';
import { ReactNode } from 'react';

// design system
import { radius, shadows, spacing, typography } from '../../../utils/designSystem';

// data
import { COLORS } from '../../../data/colors';

export interface HelperTextProps {
	/** The title/heading of the helper text */
	title: string;
	/** The description/body text */
	description: string | ReactNode;
	/** Icon to display (should be a MUI icon component) */
	icon: ReactNode;
	/** Optional color theme - defaults to info */
	color?: 'info' | 'success' | 'warning' | 'error' | 'primary';
	/** Optional additional sx styles */
	sx?: SxProps<Theme>;
}

/**
 * HelperText component for displaying contextual help information
 * at the bottom of listing pages or other sections
 */
export const HelperText = ({
	title,
	description,
	icon,
	color = 'info',
	sx,
}: HelperTextProps): JSX.Element => {
	const colorValue = COLORS[color]?.main || COLORS.info.main;

	return (
		<Paper
			elevation={0}
			sx={{
				bgcolor: alpha(colorValue, 0.05),
				border: '1px solid',
				borderColor: alpha(colorValue, 0.2),
				borderRadius: radius.lg,
				mt: spacing.lg,
				p: { xs: spacing.md, sm: spacing.lg },
				...sx,
			}}>
			<Stack direction="row" alignItems="flex-start" spacing={{ xs: spacing.sm, md: spacing.md }}>
				<Box
					sx={{
						alignItems: 'center',
						background: `linear-gradient(135deg, ${colorValue}, ${alpha(colorValue, 0.7)})`,
						borderRadius: radius.md,
						boxShadow: shadows.icon(colorValue),
						display: 'flex',
						flexShrink: 0,
						height: { xs: 36, sm: 40 },
						justifyContent: 'center',
						width: { xs: 36, sm: 40 },
					}}>
					{icon}
				</Box>
				<Box>
					<Typography
						sx={{
							...typography.bodyBold,
							fontSize: { xs: '1.35rem', sm: '1.5rem', md: '1.625rem' },
							mb: 0.5,
						}}>
						{title}
					</Typography>
					<Typography
						component="div"
						sx={{
							...typography.body,
							color: 'text.secondary',
							fontSize: { xs: '1.35rem', sm: '1.5rem', md: '1.625rem' },
						}}>
						{description}
					</Typography>
				</Box>
			</Stack>
		</Paper>
	);
};
